import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trophy, Clock, ChevronRight, LogOut, Gavel, Sparkles, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { bidderDashboard, bidderLogout, getBidderToken } from "@/lib/api";

const BidderDashboardPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getBidderToken()) { navigate("/auction"); return; }
    document.title = isNl ? "Mijn dashboard — HorseAppCompare" : "My dashboard — HorseAppCompare";
    bidderDashboard().then(setData).catch(() => navigate("/auction")).finally(() => setLoading(false));
  }, [navigate, isNl]);

  if (loading) return <main className="hac-container py-20 text-center text-[#94A3B8]">Loading…</main>;
  if (!data) return null;

  const current = data.current_auction;
  const winsCount = data.won_auctions?.length || 0;

  return (
    <main className="hac-container py-12 max-w-5xl" data-testid="bidder-dashboard">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 text-amber-600 inline-flex items-center gap-1.5"><Gavel size={12} /> {isNl ? "Mijn bieder-dashboard" : "My bidder dashboard"}</div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#0F172A]">{data.bidder.name || data.bidder.email}</h1>
          {data.bidder.company && <p className="text-[#64748B] mt-1">{data.bidder.company}</p>}
        </div>
        <div className="flex gap-2">
          <Link to="/auction" className="hac-btn-secondary" data-testid="dash-go-auction">{isNl ? "Naar veiling" : "To auction"} <ChevronRight size={14} /></Link>
          <button onClick={() => { bidderLogout(); navigate("/auction"); }} className="hac-btn-secondary text-[#64748B]" data-testid="dash-logout"><LogOut size={14} /> {isNl ? "Uitloggen" : "Logout"}</button>
        </div>
      </div>

      {/* Privacy reassurance */}
      <div className="bg-gradient-to-r from-emerald-50 to-emerald-50/30 border border-emerald-200 rounded-2xl p-4 mb-8 flex items-start gap-3" data-testid="bidder-privacy-banner">
        <div className="shrink-0 w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center"><ShieldCheck size={16} className="text-emerald-700" /></div>
        <div className="flex-1 text-sm">
          <strong className="text-emerald-900">{isNl ? "Jouw identiteit blijft privé" : "Your identity stays private"}</strong>
          <p className="text-emerald-800 mt-0.5 leading-relaxed">{isNl
            ? <>Andere bieders zien alleen <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">{(data.bidder.email || "")[0]}•••</code> bij jouw biedingen — niet je volledige e-mail of bedrijfsnaam. Bied gerust competitief zonder concurrenten te tippen.</>
            : <>Other bidders see only <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">{(data.bidder.email || "")[0]}•••</code> on your bids — not your full email or company name. Bid competitively without tipping off competitors.</>}</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        <div className="p-5 bg-white border border-[#E2E8F0] rounded-2xl">
          <div className="text-[10px] uppercase tracking-widest text-[#64748B]">{isNl ? "Veilingen gewonnen" : "Auctions won"}</div>
          <div className="text-3xl font-bold text-[#0F172A] mt-1">{winsCount}</div>
        </div>
        <div className="p-5 bg-white border border-[#E2E8F0] rounded-2xl">
          <div className="text-[10px] uppercase tracking-widest text-[#64748B]">{isNl ? "Mijn biedingen totaal" : "Total bids placed"}</div>
          <div className="text-3xl font-bold text-[#0F172A] mt-1">{data.history?.length || 0}</div>
        </div>
        <div className={`p-5 rounded-2xl border ${data.am_leading ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-200"}`}>
          <div className="text-[10px] uppercase tracking-widest text-amber-700">{isNl ? "Status huidige veiling" : "Current auction status"}</div>
          <div className={`text-lg font-bold mt-1 inline-flex items-center gap-2 ${data.am_leading ? "text-emerald-700" : "text-amber-900"}`}>
            {data.am_leading ? <><Trophy size={18} /> {isNl ? "Je leidt!" : "You're leading!"}</> : (data.my_current_bids?.length ? (isNl ? "Overboden" : "Outbid") : (isNl ? "Geen bod" : "No bid yet"))}
          </div>
        </div>
      </div>

      {/* Current auction */}
      {current && (
        <div className="bg-white border border-amber-200 rounded-2xl p-6 mb-8">
          <h2 className="font-bold text-xl text-[#0F172A] mb-1 inline-flex items-center gap-2"><Clock size={16} className="text-amber-600" /> {isNl ? "Huidige veiling" : "Current auction"}</h2>
          <p className="text-sm text-[#64748B] mb-4">{current.title}</p>
          <div className="grid sm:grid-cols-4 gap-3 mb-4">
            <div className="p-3 bg-[#F8FAFC] rounded-lg"><div className="text-[10px] text-[#64748B] uppercase">{isNl ? "Hoogste bod" : "Top bid"}</div><div className="text-xl font-bold text-amber-700">{current.current_highest_bid_eur != null ? `€${current.current_highest_bid_eur.toFixed(0)}` : "—"}</div></div>
            <div className="p-3 bg-[#F8FAFC] rounded-lg"><div className="text-[10px] text-[#64748B] uppercase">{isNl ? "Min volgend" : "Min next"}</div><div className="text-xl font-bold text-[#0F172A]">€{current.min_next_bid_eur.toFixed(0)}</div></div>
            <div className="p-3 bg-[#F8FAFC] rounded-lg"><div className="text-[10px] text-[#64748B] uppercase">Bids</div><div className="text-xl font-bold text-[#0F172A]">{current.bid_count}</div></div>
            <div className="p-3 bg-[#F8FAFC] rounded-lg"><div className="text-[10px] text-[#64748B] uppercase">{isNl ? "Sluit" : "Closes"}</div><div className="text-sm font-mono text-[#0F172A]">{new Date(current.ends_at).toLocaleString()}</div></div>
          </div>
          <Link to="/auction" className="hac-btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500" data-testid="dash-bid-now">
            <Gavel size={14} /> {data.am_leading ? (isNl ? "Bekijk veiling" : "View auction") : (isNl ? "Plaats / verhoog bod" : "Place / increase bid")}
          </Link>
        </div>
      )}

      {/* History */}
      {data.history?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-8">
          <h2 className="font-bold text-xl text-[#0F172A] mb-4">{isNl ? "Biedhistorie" : "Bid history"}</h2>
          <table className="w-full">
            <thead className="text-[10px] uppercase tracking-widest text-[#64748B]">
              <tr><th className="text-left py-2">{isNl ? "Tijdstip" : "Time"}</th><th className="text-right py-2">{isNl ? "Bedrag" : "Amount"}</th></tr>
            </thead>
            <tbody>
              {data.history.map((b, i) => (
                <tr key={i} className="border-t border-[#F1F5F9]">
                  <td className="py-2 text-sm text-[#0F172A]">{new Date(b.placed_at).toLocaleString()}</td>
                  <td className="py-2 text-right text-sm font-bold tabular-nums">€{b.amount_eur.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Won auctions */}
      {winsCount > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
          <h2 className="font-bold text-xl text-[#0F172A] mb-4 inline-flex items-center gap-2"><Trophy size={16} className="text-emerald-600" /> {isNl ? "Gewonnen veilingen" : "Won auctions"}</h2>
          <ul className="space-y-3">
            {data.won_auctions.map((a) => (
              <li key={a.id} className="bg-white border border-emerald-300 rounded-lg p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-[#0F172A]">{a.title}</div>
                    <div className="text-xs text-[#64748B]">{new Date(a.starts_at).toLocaleDateString()} → {new Date(a.ends_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-emerald-700">€{a.winner_amount_eur?.toFixed(0)}</div>
                    <div className="text-[11px] text-[#94A3B8]">{a.invoice_status === "paid" ? (isNl ? "Betaald · live" : "Paid · live") : (isNl ? "Factuur volgt" : "Invoice pending")}</div>
                  </div>
                </div>
                <Link
                  to={`/advert-submit?source=auction&auction=${encodeURIComponent(a.id)}`}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-full"
                  data-testid={`auction-upload-ad-${a.id}`}
                >
                  {isNl ? "Upload of maak je advertentie →" : "Upload or create your ad →"}
                </Link>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-[#64748B] mt-4">{isNl ? "Klik op de knop om je advertentie te uploaden of via Advert Studio te genereren. Na admin-goedkeuring gaat hij direct live." : "Click the button to upload or generate via Advert Studio. After admin approval it goes live."}</p>
        </div>
      )}
    </main>
  );
};

export default BidderDashboardPage;
